import { ClientGroup, QueryParams } from "@/sharedTypes";
import { baseApi } from "../baseApi";
import { flattenQueryParams } from "@/utils/requestUtils";

const clientGroupTag = "ClientGroup";

export const clientGroupApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getClientGroups: builder.query<any, QueryParams | void>({
      query: (params) => {
        const queryString = params
          ? `?${new URLSearchParams(flattenQueryParams(params)).toString()}`
          : "";
        return `/client-groups${queryString}`;
      },
      providesTags: [clientGroupTag],
    }),
    getClientGroupById: builder.query<ClientGroup, string>({
      query: (id) => `/client-groups/${id}`,
      transformResponse: (response: any) => response.data,
      providesTags: (result, error, id) => [{ type: clientGroupTag, id }],
    }),
    createClientGroup: builder.mutation<ClientGroup, Partial<ClientGroup>>({
      query: (data) => ({
        url: `/client-groups`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [clientGroupTag],
    }),
    updateClientGroup: builder.mutation<
      ClientGroup,
      { clientGroupId: string; updatedDetails: Partial<ClientGroup> }
    >({
      query: ({ clientGroupId, updatedDetails }) => ({
        url: `/client-groups/${clientGroupId}`,
        method: "PUT",
        body: updatedDetails,
      }),
      invalidatesTags: [clientGroupTag],
    }),
    disableClientGroup: builder.mutation<void, string>({
      query: (id) => ({
        url: `/client-groups/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [clientGroupTag],
    }),
    restoreClientGroup: builder.mutation<void, number>({
      query: (id) => ({
        url: `/client-groups/${id}/restore`,
        method: "PUT",
      }),
      invalidatesTags: [clientGroupTag],
    }),
    hardDeleteClientGroup: builder.mutation<void, number>({
      query: (id) => ({
        url: `/client-groups/${id}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: [clientGroupTag],
    }),
    getClientsInGroup: builder.query<
      any,
      { clientGroupId: number; params?: QueryParams }
    >({
      query: ({ clientGroupId, params }) => {
        const queryString = params
          ? `?${new URLSearchParams(flattenQueryParams(params)).toString()}`
          : "";
        return `/client-groups/${clientGroupId}/clients${queryString}`;
      },
      providesTags: [clientGroupTag],
    }),
    assignClientToGroup: builder.mutation<
      any,
      { clientGroupId: number; client_ids: number[] }
    >({
      query: ({ clientGroupId, client_ids }) => ({
        url: `/client-groups/${clientGroupId}/assign`,
        method: "POST",
        body: { client_ids },
      }),
      invalidatesTags: [clientGroupTag, "Client"],
    }),
    unassignClientFromGroup: builder.mutation<
      any,
      { clientGroupId: number; client_ids: number[] }
    >({
      query: ({ clientGroupId, client_ids }) => ({
        url: `/client-groups/${clientGroupId}/unassign`,
        method: "POST",
        body: { client_ids },
      }),
      invalidatesTags: [clientGroupTag, "Client"],
    }),
  }),
});

export const {
  useGetClientGroupsQuery,
  useLazyGetClientGroupsQuery,
  useGetClientGroupByIdQuery,
  useCreateClientGroupMutation,
  useUpdateClientGroupMutation,
  useDisableClientGroupMutation,
  useRestoreClientGroupMutation,
  useHardDeleteClientGroupMutation,
  useGetClientsInGroupQuery,
  useAssignClientToGroupMutation,
  useUnassignClientFromGroupMutation,
} = clientGroupApi;
